import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_IDENTITY_API ?? 'http://localhost:8087/api/v1';

export type IdentityRecord = {
  id: string;
  email: string;
  phone?: string;
  display_name: string;
  status: string;
  type: string;
  created_at: string;
  updated_at: string;
  tags?: { tag: string }[];
  metadata?: { key: string; value: string }[];
};

export type IdentityActivity = {
  id: string;
  identity_id: string;
  action: string;
  summary?: string;
  resource?: string;
  created_at: string;
};

export type IdentityAuditEntry = {
  id: string;
  identity_id: string;
  action: string;
  outcome: string;
  created_at: string;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function identityFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Identity ${path} failed: ${res.status} ${text}`);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const identityApi = {
  list: (params?: Record<string, string>) => {
    const q = params ? `?${new URLSearchParams(params)}` : '';
    return identityFetch<{ items: IdentityRecord[]; total: number }>(`/identities${q}`);
  },
  search: (params?: Record<string, string>) => {
    const q = params ? `?${new URLSearchParams(params)}` : '';
    return identityFetch<{ items: IdentityRecord[]; total: number }>(`/identities/search${q}`);
  },
  get: (id: string) => identityFetch<IdentityRecord>(`/identities/${id}`),
  create: (payload: { email: string; display_name: string; phone?: string; tags?: string[] }) =>
    identityFetch<IdentityRecord>('/identities', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id: string, payload: Record<string, unknown>) =>
    identityFetch<IdentityRecord>(`/identities/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  patch: (id: string, payload: Record<string, unknown>) =>
    identityFetch<IdentityRecord>(`/identities/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  delete: (id: string) => identityFetch<void>(`/identities/${id}`, { method: 'DELETE' }),
  merge: (id: string, targetIdentityId: string) =>
    identityFetch<IdentityRecord>(`/identities/${id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ target_identity_id: targetIdentityId }),
    }),
  restore: (id: string) => identityFetch<IdentityRecord>(`/identities/${id}/restore`, { method: 'POST' }),
  verify: (id: string, kind = 'email') =>
    identityFetch<IdentityRecord>(`/identities/${id}/verify`, { method: 'POST', body: JSON.stringify({ kind }) }),
  activity: (id: string) => identityFetch<IdentityActivity[]>(`/identities/${id}/activity`),
  audit: (id: string) => identityFetch<IdentityAuditEntry[]>(`/identities/${id}/audit`),
};
